import React, { useEffect, useState } from "react";
import api from "../services/api";
import { Link } from "react-router-dom";
import {
  FaBookmark,
  FaTrash,
  FaPlay,
  FaSpinner,
  FaExclamationTriangle,
  FaBroom,
  FaClock
} from "react-icons/fa";

function Watchlist() {
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState({});
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchWatchlist = async () => {
      try {
        setLoading(true);
        const res = await api.get("/user/watchlist");
        setWatchlist(res.data.watchlist);
      } catch (err) {
        console.error("Error fetching watchlist");
        setError("Failed to load watchlist");
      } finally {
        setLoading(false);
      }
    };

    fetchWatchlist();
  }, []);

  const handleRemove = async (movieId) => {
    setRemoving(prev => ({ ...prev, [movieId]: true }));
    try {
      const res = await api.delete(`/user/watchlist/${movieId}`);
      setWatchlist(res.data.watchlist);
    } catch (err) {
      console.error("Error removing watchlist item");
      setError("Failed to remove from watchlist");
    } finally {
      setRemoving(prev => ({ ...prev, [movieId]: false }));
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Are you sure you want to clear your entire watchlist? This action cannot be undone.")) {
      return;
    }

    setClearing(true);
    try {
      const res = await api.delete("/user/watchlist/clear");
      setWatchlist(res.data.watchlist);
    } catch (err) {
      console.error("Error clearing watchlist");
      setError("Failed to clear watchlist");
    } finally {
      setClearing(false);
    }
  };

  const MovieCard = ({ movie, index }) => (
    <div className="group relative bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
      <Link to={`/movie/${movie.movieId}`} className="block">
        <div className="relative overflow-hidden">
          {movie.posterPath ? (
            <img
              src={`https://image.tmdb.org/t/p/w500${movie.posterPath}`}
              alt={movie.title}
              className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-80 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <FaPlay className="text-4xl mx-auto mb-2" />
                <span className="text-sm">No Image Available</span>
              </div>
            </div>
          )}

          {/* Overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <FaPlay className="text-white text-3xl bg-white/20 rounded-full p-3 backdrop-blur-sm" />
            </div>
          </div>

          {/* Bookmark Badge */}
          <div className="absolute top-3 left-3 bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
            <FaBookmark className="text-xs" />
            <span>Watch Later</span>
          </div>
        </div>

        <div className="p-4">
          <h3 className="font-bold text-gray-900 text-lg mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
            {movie.title}
          </h3>
        </div>
      </Link>

      {/* Remove Button */}
      <div className="absolute bottom-4 right-4">
        <button
          onClick={(e) => {
            e.preventDefault();
            handleRemove(movie.movieId);
          }}
          disabled={removing[movie.movieId]}
          className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {removing[movie.movieId] ? (
            <FaSpinner className="animate-spin text-sm" />
          ) : (
            <FaTrash className="text-sm" />
          )}
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="text-4xl text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your watchlist...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 flex items-center justify-center space-x-4">
            <FaBookmark className="text-blue-200" />
            <span>My Watchlist</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90">
            {watchlist.length > 0
              ? `You have ${watchlist.length} movie${watchlist.length !== 1 ? 's' : ''} to watch`
              : "Movies you want to watch will appear here"
            }
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center space-x-2">
            <FaExclamationTriangle />
            <span>{error}</span>
          </div>
        )}

        {watchlist.length > 0 && (
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Movies to Watch
              </h2>
              <p className="text-gray-600">
                {watchlist.length} movie{watchlist.length !== 1 ? 's' : ''} in your watchlist
              </p>
            </div>

            <button
              onClick={handleClear}
              disabled={clearing}
              className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {clearing ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaBroom />
              )}
              <span>Clear All</span>
            </button>
          </div>
        )}

        {watchlist.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {watchlist.map((movie, index) => (
              <MovieCard key={movie.movieId} movie={movie} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📺</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Your Watchlist is Empty
            </h3>
            <p className="text-gray-600 mb-8">
              Start building your watchlist by adding movies you want to watch later
            </p>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 bg-blue-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
            >
              <FaBookmark />
              <span>Discover Movies</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default Watchlist;